// /utils/reports/proval/samDefectsShort.js

/**
 * SAM Defects Short Continuous Export
 * 
 * Source: LR (Localized Roughness) events - 25ft continuous ALR analysis
 * Output: Two tables (Left Profile, Right Profile) in same file
 * 
 * Note: Events don't have an exception flag - they're only created
 * for valid data areas, so no filtering needed.
 */

// ============================================
// HELPERS
// ============================================

const WHEELPATH = {
  LEFT: ['Left', 'L', 'left'],
  RIGHT: ['Right', 'R', 'right'],
};

function sanitizeFilename(str) {
  return str.replace(/[^a-zA-Z0-9-_]/g, '_').substring(0, 50);
}

function getSessionStart(segments) {
  const sorted = [...(segments || [])].sort((a, b) => a.from_station_ft - b.from_station_ft);
  return sorted[0]?.from_station_ft ?? 0;
}

function filterLREvents(events) {
  return (events || [])
    .filter(e => e.event_type === 'LR')
    .sort((a, b) => a.from_station_ft - b.from_station_ft);
}

function splitByWheelpath(events) {
  return {
    left: events.filter(e => WHEELPATH.LEFT.includes(e.wheelpath)),
    right: events.filter(e => WHEELPATH.RIGHT.includes(e.wheelpath)),
  };
}

function formatEventRow(event, sessionStart, delimiter) {
  return [
    (event.from_station_ft - sessionStart).toFixed(1),
    (event.to_station_ft - sessionStart).toFixed(1),
    event.magnitude_in?.toFixed(2) ?? '',
  ].join(delimiter);
}

function buildProfileTable(label, events, sessionStart, delimiter) {
  const headers = ['Start Distance (ft)', 'Stop Distance (ft)', 'IRI (in/mi)'];
  const lines = [label, headers.join(delimiter)];

  if (events.length === 0) {
    lines.push('(No defects)');
  } else {
    events.forEach(evt => {
      lines.push(formatEventRow(evt, sessionStart, delimiter));
    });
  }

  return lines;
}

// ============================================
// EXPORTS
// ============================================

export function generateSAMDefectsShort(events, segments, options = {}) {
  const { format = 'txt' } = options;
  const delimiter = format === 'csv' ? ',' : '\t';

  const sessionStart = getSessionStart(segments);
  const lrEvents = filterLREvents(events);
  const { left, right } = splitByWheelpath(lrEvents);

  const leftTable = buildProfileTable('Left Profile', left, sessionStart, delimiter);
  const rightTable = buildProfileTable('Right Profile', right, sessionStart, delimiter);

  return [...leftTable, '', ...rightTable].join('\n');
}

export function getSAMDefectsShortFilename(session, format = 'txt') {
  const name = sanitizeFilename(session?.name || 'Session');
  return `SAM_Defects_Short_Continuous_${name}.${format}`;
}